import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

// Magic-link landing route. Supabase's detectSessionInUrl (enabled in the
// client) parses the auth tokens from the redirect URL and stores the session.
// We just wait for the session to settle, then forward into the gated app.
//
// Handles both the PKCE code flow (?code=...) and the implicit hash flow
// (#access_token=...). exchangeCodeForSession is a no-op / harmless if there's
// no code param.
export default function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function finalize() {
      // PKCE flow: exchange the ?code for a session. Ignore "no code" errors;
      // the implicit/hash flow is handled by detectSessionInUrl automatically.
      const params = new URLSearchParams(window.location.search);
      if (params.get("code")) {
        const { error: exchErr } = await supabase.auth.exchangeCodeForSession(
          window.location.href
        );
        if (exchErr && active) {
          setError(exchErr.message);
          return;
        }
      }

      const { data } = await supabase.auth.getSession();
      if (!active) return;
      if (data.session) {
        navigate("/app", { replace: true });
      } else {
        // No session resolved — link expired or already used.
        setError(
          "We couldn't complete sign-in. The link may have expired — request a new one."
        );
      }
    }

    finalize();
    return () => {
      active = false;
    };
  }, [navigate]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 px-6 text-center">
      <div className="max-w-md">
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-txcfi-ink">
              Sign-in problem
            </h1>
            <p className="mt-2 text-sm text-slate-600">{error}</p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="mt-4 rounded-md bg-txcfi-accent px-4 py-2 text-sm font-medium text-white"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <p className="text-slate-500">Signing you in…</p>
        )}
      </div>
    </main>
  );
}
